import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ClockIcon, MessageCircleIcon, SearchIcon, WifiOffIcon } from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { founder, salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { ease } from '../../core/format/motion';
import { demoOrderNote } from '../../utils/api';
import { orderHref } from '../../utils/accountOrders';
import { OrderPlaced } from './OrderPlaced';
import { useOrderReturn } from './useOrderReturn';
import { takeOrderToken } from './orderToken';
import type { ReturnPhase } from './useOrderReturn';
import type { IconComponent } from '../../ui/icon';
type Copy = {
  title: string;
  text: string;
  icon: IconComponent | null;
};
const COPY: Record<Exclude<ReturnPhase, 'paid'>, Copy> = {
  checking: {
    icon: null,
    title: 'Even kijken of je betaling binnen is',
    text: 'Dit duurt meestal maar een paar seconden.'
  },
  waiting: {
    icon: ClockIcon,
    title: 'Je betaling wordt nog verwerkt',
    text: 'Je bank heeft hem nog niet bevestigd. Dat duurt soms een paar minuten. Zodra hij binnen is, krijg je een mail. Opnieuw betalen hoeft niet.'
  },
  failed: {
    icon: AlertCircleIcon,
    title: 'Je betaling is niet gelukt',
    text: 'Er is niets afgeschreven en je winkelmand staat nog klaar. Probeer het gerust opnieuw.'
  },
  expired: {
    icon: ClockIcon,
    title: 'Je betaling is verlopen',
    text: 'Het betaalscherm stond te lang open. Er is niets afgeschreven en je winkelmand staat nog klaar.'
  },
  notFound: {
    icon: SearchIcon,
    title: 'We vinden deze bestelling niet',
    text: 'De link is misschien verlopen of niet helemaal overgenomen. Is je betaling gelukt, dan staat de bevestiging in je mail. Met een account vind je hem ook in Mijn omgeving.'
  },
  error: {
    icon: WifiOffIcon,
    title: 'We konden je betaling even niet controleren',
    text: 'Geen zorgen: is hij gelukt, dan krijg je een mail. Probeer het zo nog eens.'
  }
};
const PAID_WITHOUT_DETAILS: Copy = {
  icon: CheckIcon,
  title: 'Je betaling is gelukt',
  text: 'Dank je wel! De bevestiging staat in je mail.'
};
const quietLink = 'inline-flex items-center gap-2 py-2 text-[14px] text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline';

/**
 * /bestelling/:orderId?t=<token> — waar Mollie de klant na het betalen naartoe stuurt.
 * Zelfde opbouw als het bedankscherm: een rond statusteken, een kop, één zin uitleg,
 * daaronder de acties, en onderaan altijd het bestelnummer en een vraag aan Sarah.
 */
export function OrderReturn() {
  const {
    orderId = ''
  } = useParams();
  // Het ondertekende token uit de terugkeerlink: zo werkt de pagina ook in een andere browser (bank-app).
  // Eén keer gelezen en meteen uit de adresbalk gehaald (geschiedenis, schermdelen); verversen werkt nog.
  const [token] = useState(() => takeOrderToken(orderId));
  const r = useOrderReturn(orderId, token);
  const reduce = useReducedMotion();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Elke nieuwe stand: focus op de kop, zodat schermlezers hem voorlezen. Geen zichtbare ring (zie index.css).
  useEffect(() => {
    headingRef.current?.focus({
      preventScroll: true
    });
  }, [r.phase]);
  if (r.phase === 'paid' && r.placed) return <OrderPlaced placed={r.placed} headingRef={headingRef} token={token} data-id="element-7110" data-mp="i0nj2zwwg63v" />;
  const copy = r.phase === 'paid' ? PAID_WITHOUT_DETAILS : COPY[r.phase];
  const Icon = copy.icon;
  const canRetry = r.phase === 'failed' || r.phase === 'expired';
  const calm = r.phase === 'paid' || r.phase === 'checking';
  return <section className="bg-sand" data-id="element-7111" data-mp="27f2c3weyaf56">
      <div className="mx-auto max-w-2xl px-6 pb-28 pt-12 lg:px-10 lg:pt-20" aria-busy={r.phase === 'checking'} data-id="element-7112" data-mp="27eadsdtyy3b7">
        <motion.div key={r.phase} initial={reduce ? false : {
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-7113" data-mp="22g8x1zk0mg2w">
          <span aria-hidden="true" className={`flex h-12 w-12 items-center justify-center rounded-full ${r.phase === 'paid' ? 'bg-ink text-shell' : 'bg-shell text-clay ring-1 ring-line'}`} data-id="element-7114" data-mp="gxyg94d07eaa">
            {Icon ? <Icon className="h-5 w-5" strokeWidth={1.5} data-id="element-7115" data-mp="q0pu1atcqm53" /> : <Spinner className="h-5 w-5" data-id="element-7116" data-mp="uy9amhdivb07" />}
          </span>

          <h1 ref={headingRef} tabIndex={-1} className="mt-7 max-w-xl font-display text-[2.2rem] font-light leading-[1.08] text-ink sm:text-5xl" data-id="element-7117" data-mp="uxzf6e9pkbn4">
            {copy.title}
          </h1>
          <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" role="status" data-id="element-7118" data-mp="14pdotevdirtz">
            {copy.text}
          </p>

          {/* DEMO: met een backend is demoOrderNote null en verdwijnt dit vak vanzelf. */}
          {r.phase === 'notFound' && demoOrderNote && <p className="mt-5 max-w-xl rounded-sm border border-dashed border-line px-4 py-3 text-[14px] leading-relaxed text-muted" data-id="element-7119" data-mp="1iovf5yobid5g">{demoOrderNote}</p>}

          {r.retryError && <p role="alert" className="mt-5 max-w-xl text-[15px] text-clay" data-id="element-7120" data-mp="2y6i5r15xreat">
              {r.retryError}
            </p>}

          {r.phase !== 'checking' && <div className="mt-9 flex flex-wrap items-center gap-3" data-id="element-7121" data-mp="2p6d2923v501x">
              {canRetry && <>
                  <button type="button" className={btnPrimary} onClick={() => void r.retry()} disabled={r.retrying} data-id="element-7122" data-mp="3lwm0sk5265q7">
                    {r.retrying && <Spinner data-id="element-7123" data-mp="3hvka4rp7j1zu" />}
                    {r.retrying ? 'Even geduld…' : 'Opnieuw betalen'}
                  </button>
                  <Link to="/afrekenen" className={btnSecondary} data-id="element-7124" data-mp="384z2kerk2n4s">
                    Andere betaalmethode
                  </Link>
                </>}
              {(r.phase === 'waiting' || r.phase === 'error') && <>
                  <button type="button" className={btnPrimary} onClick={r.recheck} data-id="element-7125" data-mp="4ngxug72xw8l">
                    Opnieuw controleren
                  </button>
                  <Link to="/webshop" className={btnSecondary} data-id="element-7126" data-mp="sjkgls90bk45">
                    Verder winkelen
                  </Link>
                </>}
              {r.phase === 'notFound' && <>
                  <Link to="/account/bestellingen" className={btnPrimary} data-id="element-7127" data-mp="iszcxqeh7ped">
                    {r.signedIn ? 'Naar je bestellingen' : 'Inloggen bij Mijn omgeving'}
                  </Link>
                  <Link to="/webshop" className={btnSecondary} data-id="element-7128" data-mp="9tm0qpyywmpy">
                    Verder winkelen
                  </Link>
                </>}
              {r.phase === 'paid' && <>
                  {r.signedIn && <Link to={orderHref(orderId)} className={btnPrimary} data-id="element-7129" data-mp="3w2dhb4gosegw">
                      Bekijk je bestelling
                    </Link>}
                  <Link to="/webshop" className={r.signedIn ? btnSecondary : btnPrimary} data-id="element-7130" data-mp="1u7wg047hwf4q">
                    Verder winkelen
                  </Link>
                </>}
            </div>}
        </motion.div>

        {/* Het nette bestelnummer als het er is (na betalen); nooit het technische id. */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-x-8 gap-y-2 border-t border-line pt-6" data-id="element-7131" data-mp="3oi2wh5n95r9l">
          <p className="text-[14px] text-muted" data-id="element-7132" data-mp="3jo8f48v4dodo">
            {r.orderNumber ? <>
                Bestelling <span className="font-medium tabular-nums text-ink" data-id="element-7133" data-mp="3tfuy4rdp8rqz">{r.orderNumber}</span>
              </> : shopLabels.orderNumberLater}
          </p>
          {!calm && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={quietLink} data-id="element-7134" data-mp="6an8n5jpf0v2">
              <MessageCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7135" data-mp="2d1cuxztrxnmb" />
              Twijfel je? App {founder.firstName}
              <span className="sr-only" data-id="element-7136" data-mp="284cqftckosxb">{shopLabels.viaWhatsApp}</span>
            </a>}
        </div>
      </div>
    </section>;
}